import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';

// Create the context
const PathHistoryContext = createContext(null);

/**
 * Provider component for path history tracking
 * Manages navigation path through the subtask flow
 * Automatically resets when component unmounts (e.g., when leaving subtask flow)
 */
export const PathHistoryProvider = ({ children }) => {
  const [pathHistory, setPathHistory] = useState({
    startTime: new Date().toISOString(),
    path: [],
  });

  // Add a step to the path history
  const addStep = useCallback(stepName => {
    setPathHistory(prev => ({
      ...prev,
      path: [
        ...prev.path,
        {
          step: stepName,
          timestamp: new Date().toISOString(),
        },
      ],
    }));
  }, []);

  // Get current path history
  const getHistory = useCallback(() => pathHistory, [pathHistory]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      pathHistory,
      addStep,
      getHistory,
    }),
    [pathHistory, addStep, getHistory],
  );

  return (
    <PathHistoryContext.Provider value={value}>
      {children}
    </PathHistoryContext.Provider>
  );
};

PathHistoryProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to access path history context
 * Must be used within PathHistoryProvider
 *
 * @returns {Object} Context value with pathHistory, addStep, and getHistory
 * @throws {Error} If used outside of PathHistoryProvider
 */
export const usePathHistory = () => {
  const context = useContext(PathHistoryContext);
  if (!context) {
    throw new Error('usePathHistory must be used within PathHistoryProvider');
  }
  return context;
};
